import React, { useEffect, useRef } from 'react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { ListItemNode, ListNode } from '@lexical/list'
import { LinkNode } from '@lexical/link'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { TablePlugin } from '@lexical/react/LexicalTablePlugin'
import { TableNode, TableCellNode, TableRowNode } from '@lexical/table'
import ToolbarPlugin from './ToolbarPlugin'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { EditorState, $getRoot, $insertNodes } from 'lexical'

interface LexicalEditorComponentProps {
	initialContent?: string
	onContentChange: (content: string) => void
}

const initialConfig = {
	namespace: 'MindclineEditor',
	theme: {},
	onError: (error: Error) => {
		console.error('Lexical Editor Error:', error)
	},
	nodes: [
		HeadingNode,
		ListNode,
		ListItemNode,
		QuoteNode,
		LinkNode,
		TableNode,
		TableCellNode,
		TableRowNode,
	],
}

const ContentInitializer: React.FC<{ content: string }> = ({ content }) => {
	const [editor] = useLexicalComposerContext()
	const initializedRef = useRef(false)

	useEffect(() => {
		if (!content || initializedRef.current) return

		initializedRef.current = true

		editor.update(() => {
			const root = $getRoot()

			// 기존 콘텐츠를 클리어
			root.clear()

			// DB의 <img> 태그를 [IMAGE:URL:ALT] 마커로 변환 (에디터 내부 표현용)
			let processedContent = content

			// <img> 태그를 [IMAGE:URL:ALT] 마커로 변환 (src, alt 속성 순서 무관)
			// src가 먼저 오는 경우
			processedContent = processedContent.replace(
				/<img[^>]+src\s*=\s*["']([^"']*)["'][^>]*alt\s*=\s*["']([^"']*)["'][^>]*\/?>/gi,
				(match, src, alt) => {
					return `[IMAGE:${src}:${alt || '이미지'}]`
				}
			)

			// alt가 먼저 오는 경우
			processedContent = processedContent.replace(
				/<img[^>]+alt\s*=\s*["']([^"']*)["'][^>]*src\s*=\s*["']([^"']*)["'][^>]*\/?>/gi,
				(match, alt, src) => {
					return `[IMAGE:${src}:${alt || '이미지'}]`
				}
			)

			// alt 속성이 없는 경우
			processedContent = processedContent.replace(
				/<img[^>]+src\s*=\s*["']([^"']*)["'][^>]*\/?>/gi,
				(match, src) => {
					// 이미 변환된 것은 건너뛰기
					if (match.startsWith('[IMAGE:')) return match
					return `[IMAGE:${src}:이미지]`
				}
			)

			if (processedContent.trim()) {
				try {
					const parser = new DOMParser()
					const dom = parser.parseFromString(processedContent, 'text/html')
					const nodes = $generateNodesFromDOM(editor, dom)

					if (nodes.length > 0) {
						$insertNodes(nodes)
					}
				} catch (error) {
					console.error('Failed to parse content:', error)
				}
			}
		})
	}, [editor, content])

	return null
}

const LexicalEditorComponent: React.FC<LexicalEditorComponentProps> = ({
	initialContent = '',
	onContentChange,
}) => {
	const lastContentRef = useRef(initialContent)
	const isInternalUpdateRef = useRef(false)

	return (
		<LexicalComposer initialConfig={initialConfig}>
			<ContentInitializer content={initialContent} />
			<ToolbarPlugin />
			<div style={{ position: 'relative' }}>
				<RichTextPlugin
					contentEditable={
						<ContentEditable
							style={{
								minHeight: '350px',
								padding: '16px',
								outline: 'none',
								fontSize: '14px',
								lineHeight: '1.6',
								border: 'none',
								background: 'transparent',
								fontFamily: 'inherit',
								width: '100%',
								boxSizing: 'border-box',
								display: 'block',
								color: '#24292f',
							}}
							spellCheck={false}
						/>
					}
					placeholder={
						<div
							style={{
								position: 'absolute',
								top: '66px',
								left: '16px',
								color: '#656d76',
								pointerEvents: 'none',
								fontSize: '14px',
								userSelect: 'none',
							}}
						>
							내용을 입력하세요...
						</div>
					}
					ErrorBoundary={LexicalErrorBoundary}
				/>
				<HistoryPlugin />
				<ListPlugin />
				<LinkPlugin />
				<TablePlugin />
				<OnChangePlugin
					onChange={(editorState: EditorState, editor) => {
						if (isInternalUpdateRef.current) {
							isInternalUpdateRef.current = false
							return
						}

						editorState.read(() => {
							let htmlString = $generateHtmlFromNodes(editor, null)

							// 이미지 마커를 실제 HTML img 태그로 변환 (DB 저장용)
							htmlString = htmlString.replace(
								/\[IMAGE:([^\]]+)\]/g,
								(match, content) => {
									// 마지막 콜론을 기준으로 URL과 ALT 텍스트 분리
									const lastColonIndex = content.lastIndexOf(':')
									let src, alt

									if (lastColonIndex !== -1 && lastColonIndex < content.length - 1) {
										// 마지막 콜론이 존재하고 그 뒤에 내용이 있으면
										src = content.substring(0, lastColonIndex)
										alt = content.substring(lastColonIndex + 1)
									} else {
										// 콜론이 없거나 마지막에 있으면 전체를 URL로 사용
										src = content
										alt = '이미지'
									}

									return `<img src="${src}" alt="${alt}" style="max-width: 100%; height: auto; display: block; margin: 10px 0;" />`
								}
							)

							if (htmlString !== lastContentRef.current) {
								lastContentRef.current = htmlString
								onContentChange(htmlString)
							}
						})
					}}
				/>
			</div>
		</LexicalComposer>
	)
}

export default LexicalEditorComponent
